import { createContext, useContext, type ReactNode } from "react";
import { getBrandTheme, resolveBrandId } from "./registry";
import { BRAND_METADATA } from "./metadata";
import type { BrandAssets, BrandThemeConfig } from "./types";

// BrandProvider — one provider, wrapping the whole web-client.
//
// The context defaults to the brand this build was configured for instead of
// being undefined-until-provided. That keeps every existing test (and any
// component rendered in isolation) working with no wrapper, and means a
// missing provider degrades to the configured brand rather than throwing.

const defaultTheme = getBrandTheme(resolveBrandId(import.meta.env.VITE_BRAND));

const BrandContext = createContext<BrandThemeConfig>(defaultTheme);

export function BrandProvider({ theme, children }: { theme: BrandThemeConfig; children: ReactNode }) {
  return <BrandContext.Provider value={theme}>{children}</BrandContext.Provider>;
}

/** The active brand: id, label, colour tokens and assets. */
export function useBrand(): BrandThemeConfig {
  return useContext(BrandContext);
}

/** Just the assets — the common case at a call site. */
export function useBrandAssets(): BrandAssets {
  return useContext(BrandContext).assets;
}

/**
 * The active brand's display NAME, for user-facing copy to interpolate.
 *
 * Copy is deliberately NOT part of BrandThemeConfig (see types.ts) — the name
 * lives in BRAND_METADATA, which src/lib/seo.ts and vite.config.ts read too, so
 * a string a user reads and a string a crawler reads can never disagree. This
 * hook is the React-side door to it, resolved through the same context as the
 * logos so a provider override moves the name and the artwork together.
 *
 * Any copy that names the platform takes `{{brand}}` and this value. A literal
 * brand name in a locale file is a leak: it ships to EVERY brand's bundle, and
 * the strings that carry it hardest are the outbound ones — an aria-label, a
 * share message posted to X — where the wrong name leaves the platform.
 */
export function useBrandName(): string {
  return BRAND_METADATA[useBrand().id].name;
}
